export type AddonQuantityEntry = {
  id: number;
  quantity: number;
  Name?: string;
  Premium?: string | number | null;
};

/** Collapse policy addon rows into unique ids with quantities for form hydrate. */
export const normalizePolicyAddonsForForm = (
  policyAddons: any[] = []
): AddonQuantityEntry[] => {
  const byId = new Map<number, AddonQuantityEntry>();

  for (const item of policyAddons || []) {
    const id = Number(item?.id ?? item?.addonId);
    if (!id || Number.isNaN(id)) continue;

    const qtyRaw = item?.quantity ?? item?.Quantity;
    const qty =
      qtyRaw != null && qtyRaw !== ""
        ? Math.max(0, Number(qtyRaw) || 0)
        : 1;

    const existing = byId.get(id);
    if (existing) {
      existing.quantity += qty;
    } else {
      byId.set(id, {
        id,
        quantity: qty,
        Name: item?.Name,
        Premium: item?.Premium ?? item?.PolicyPremium ?? null,
      });
    }
  }

  return Array.from(byId.values());
};

/** Build POST/PATCH Addons payload — only quantity > 0. */
export const toAddonsPayload = (
  addons: any[] | null | undefined
): { id: number; quantity: number }[] => {
  if (!Array.isArray(addons)) return [];

  return addons
    .map((addon) => {
      if (addon == null) return null;

      if (typeof addon === "number" || typeof addon === "string") {
        const id = Number(addon);
        if (!id || Number.isNaN(id)) return null;
        return { id, quantity: 1 };
      }

      const id = Number(addon.id ?? addon.value);
      if (!id || Number.isNaN(id)) return null;

      const quantity = Math.max(0, Number(addon.quantity ?? addon.Quantity ?? 0) || 0);
      if (quantity <= 0) return null;

      return { id, quantity };
    })
    .filter((entry): entry is { id: number; quantity: number } => entry != null);
};

/** Merge product-linked addons with current form quantities. */
export const mergeAddonQuantities = (
  linkedAddons: any[] = [],
  current: any[] = []
): AddonQuantityEntry[] => {
  const qtyById = new Map<number, number>();
  for (const entry of current || []) {
    const id = Number(entry?.id ?? entry?.value);
    if (!id || Number.isNaN(id)) continue;
    const quantity = Math.max(
      0,
      Number(entry?.quantity ?? entry?.Quantity ?? 0) || 0
    );
    qtyById.set(id, (qtyById.get(id) || 0) + quantity);
  }

  const seen = new Set<number>();
  const merged: AddonQuantityEntry[] = [];

  for (const addon of linkedAddons || []) {
    const id = Number(addon?.id);
    if (!id || Number.isNaN(id) || seen.has(id)) continue;
    seen.add(id);
    merged.push({
      id,
      Name: addon.Name,
      Premium: addon.Premium ?? addon.PolicyPremium ?? null,
      quantity: qtyById.get(id) ?? 0,
    });
  }

  // Keep any hydrated addons no longer linked to the product so they can be zeroed out
  for (const [id, quantity] of qtyById) {
    if (seen.has(id) || quantity <= 0) continue;
    const existing = (current || []).find(
      (entry) => Number(entry?.id ?? entry?.value) === id
    );
    merged.push({
      id,
      Name: existing?.Name ?? existing?.label ?? `Addon #${id}`,
      Premium: existing?.Premium ?? null,
      quantity,
    });
  }

  return merged;
};
